// The customer record's everyday edits (office-history prototype), each a sheet over the page:
// details, a new contact, a phone's or email's consent, an address, equipment, tags, a note on a
// job and an office file on a job. Every write is one existing action; what a role may change is
// decided by the server, so a refusal shows the server's words.

import { useEffect, useId, useState } from 'react';
import { client } from '../api.ts';
import { saveConsent } from './consent-save.ts';
import { phoneText, type ContactMethod, type Customer, type Equipment } from '../types.ts';
import { newId, SaveButton } from '../shared/SaveButton.tsx';
import { Sheet, SheetButtons as Buttons, useRebase, useWrite } from '../shared/EditSheet.tsx';
import { MIRROR_LINE } from '../shared/mirror.ts';
import {
  CONSENT_SOURCES,
  FILE_ACCEPT,
  FILE_CATEGORIES,
  consentForm,
  contactInput,
  detailsForm,
  detailsInput,
  hasWholeAddress,
  consentLine,
  renameInput,
  changeMethodInput,
  emptyMethodForm,
  methodInput,
  type MethodForm,
  emptyContactForm,
  emptyEquipmentForm,
  equipmentChangeInput,
  equipmentForm,
  equipmentInput,
  retireInput,
  fileKind,
  emptyLocationForm,
  locationForm,
  locationInput,
  ownLocationInputs,
  newLocationInput,
  siteContactChoices,
  someoneInput,
  SOMEONE_ELSE,
  noteEditInput,
  TERMS_DAYS,
  taxExemptForm,
  taxExemptInput,
  termsForm,
  termsInput,
  noteInput,
  customerNoteInput,
  reopenInput,
  refusal,
  type AddressForm,
  type ConsentSource,
} from './customer-edit-model.ts';

type Loc = Customer['locations'][number];

// ── an address's parts, as typed ──

function AddressFields({
  a,
  onChange,
  required,
}: {
  a: AddressForm;
  onChange: (a: AddressForm) => void;
  /** The street and city can't be emptied (a service address); a billing address can go entirely. */
  required?: boolean;
}) {
  const set = (k: keyof AddressForm) => (e: { target: { value: string } }) =>
    onChange({ ...a, [k]: e.target.value });
  return (
    <div className="fgrid">
      <label className="f">
        Street
        <input className="field" value={a.line1} onChange={set('line1')} required={required} />
      </label>
      <label className="f">
        Unit or suite
        <input className="field" value={a.line2} onChange={set('line2')} placeholder="optional" />
      </label>
      <label className="f">
        City
        <input className="field" value={a.city} onChange={set('city')} required={required} />
      </label>
      <label className="f">
        Province
        <input className="field" value={a.region} onChange={set('region')} placeholder="optional" />
      </label>
      <label className="f">
        Postal code
        <input className="field" value={a.postal} onChange={set('postal')} placeholder="optional" />
      </label>
    </div>
  );
}

// ── details ──

export function EditDetails({
  customer,
  onSaved,
  onClose,
  onLatest,
}: {
  customer: Customer;
  onSaved: (c: Customer) => void;
  onClose: () => void;
  /** Read the customer again after someone else's change (F-53). */
  onLatest?: () => Promise<unknown>;
}) {
  const [f, setF] = useState(() => detailsForm(customer));
  useRebase(detailsForm(customer), customer.rowVersion, setF);
  const w = useWrite(JSON.stringify(f));
  const save = async () => {
    const c = await w.run<Customer>('customers.update', detailsInput(customer, f));
    if (c) onSaved(c);
  };
  return (
    <Sheet title={`Edit ${customer.displayName}`} onClose={onClose}>
      <div className="fgrid">
        <label className="f">
          Name
          <input
            className="field"
            value={f.displayName}
            onChange={(e) => setF({ ...f, displayName: e.target.value })}
            autoFocus
          />
        </label>
        <label className="f">
          Type
          <select
            className="field"
            value={f.kind}
            onChange={(e) => setF({ ...f, kind: e.target.value as Customer['kind'] })}
          >
            <option value="residential">Residential</option>
            <option value="commercial">Commercial</option>
          </select>
        </label>
        <label className="f">
          Status
          <select
            className="field"
            value={f.status}
            onChange={(e) => setF({ ...f, status: e.target.value as Customer['status'] })}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive (no new work)</option>
          </select>
        </label>
      </div>
      <label className="checkrow">
        <input
          type="checkbox"
          checked={f.doNotContact}
          onChange={(e) => setF({ ...f, doNotContact: e.target.checked })}
        />{' '}
        Do not contact this customer (no texts, emails or reminders)
      </label>
      {customer.notes !== undefined && (
        <label className="f">
          Office notes
          <textarea
            className="field area"
            value={f.notes}
            onChange={(e) => setF({ ...f, notes: e.target.value })}
            maxLength={4000}
            placeholder="What everyone in the office should know about them"
          />
        </label>
      )}
      {customer.billingAddress !== undefined && (
        <fieldset className="plain">
          <legend className="flabel">Billing address (if not the service address)</legend>
          <AddressFields a={f.billing} onChange={(billing) => setF({ ...f, billing })} />
          <p className="muted small">Empty, invoices and statements go to the service address.</p>
        </fieldset>
      )}
      <Buttons
        w={w}
        {...(onLatest ? { onLatest } : {})}
        label="Save"
        busyLabel="Saving…"
        disabled={!f.displayName.trim()}
        onSave={() => void save()}
        onCancel={onClose}
      />
    </Sheet>
  );
}

// ── a new contact ──

function SourcePicker({ value, onChange }: { value: ConsentSource; onChange: (s: ConsentSource) => void }) {
  return (
    <label className="f">
      How they agreed
      <select className="field" value={value} onChange={(e) => onChange(e.target.value as ConsentSource)}>
        {CONSENT_SOURCES.map(([k, label]) => (
          <option key={k} value={k}>
            {label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function AddContact({
  customer,
  onSaved,
  onClose,
}: {
  customer: Customer;
  onSaved: (c: Customer) => void;
  onClose: () => void;
}) {
  const [f, setF] = useState(emptyContactForm);
  const w = useWrite(JSON.stringify(f));
  const consentId = useId();
  const set = (k: keyof typeof f) => (e: { target: { value: string; checked?: boolean; type?: string } }) => {
    setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
    w.setError(null);
  };
  const save = async () => {
    const c = await w.run<Customer>('customers.addContact', contactInput(customer.id, f));
    if (c) onSaved(c);
  };
  return (
    <Sheet title={`Add a contact for ${customer.displayName}`} onClose={onClose}>
      <div className="fgrid">
        <label className="f">
          Name
          <input className="field" value={f.name} onChange={set('name')} autoFocus />
        </label>
        <label className="f">
          Role
          <input
            className="field"
            value={f.title}
            onChange={set('title')}
            placeholder="optional, like Tenant or Property manager"
          />
        </label>
        <label className="f">
          Phone
          <input
            className="field"
            type="tel"
            value={f.phone}
            onChange={set('phone')}
            placeholder="optional"
          />
        </label>
        <label className="f">
          Email
          <input
            className="field"
            type="email"
            value={f.email}
            onChange={set('email')}
            placeholder="optional"
          />
        </label>
      </div>
      <div className="nc-consent" role="group" aria-labelledby={consentId}>
        <div className="flabel" id={consentId}>
          Consent (saved with who and when)
        </div>
        <label className="checkrow">
          <input type="checkbox" checked={f.sms} onChange={set('sms')} disabled={!f.phone.trim()} /> OK to
          text about their appointments and invoices
        </label>
        <label className="checkrow">
          <input type="checkbox" checked={f.mail} onChange={set('mail')} disabled={!f.email.trim()} /> OK to
          email
        </label>
        {(f.sms || f.mail) && <SourcePicker value={f.source} onChange={(source) => setF({ ...f, source })} />}
      </div>
      <Buttons
        w={w}
        label="Add contact"
        busyLabel="Adding the contact…"
        disabled={!f.name.trim()}
        onSave={() => void save()}
        onCancel={onClose}
      />
    </Sheet>
  );
}

// ── one phone's or email's consent ──

export function EditConsent({
  customer,
  contactName,
  method,
  onSaved,
  onClose,
}: {
  customer: Customer;
  contactName: string;
  method: ContactMethod;
  onSaved: (c: Customer) => void;
  onClose: () => void;
}) {
  const [f, setF] = useState(() => consentForm(method));
  const w = useWrite(JSON.stringify(f));
  const phone = method.kind === 'phone';
  // while mirroring, a phone's texts-off is kept here and the sheet says so (QA-615, consent-save.ts)
  const [kept, setKept] = useState<string | null>(null);
  const save = async () => {
    const r = await saveConsent(w, method, f);
    if (r.kept) setKept(r.kept);
    else if (r.saved) onSaved(r.saved);
  };
  const shown = phone ? phoneText(method.value) : method.value;
  return (
    <Sheet title={`${contactName} · ${shown}`} onClose={onClose}>
      {customer.doNotContact && (
        <p className="notice warn">
          The whole customer is marked do not contact, so nothing goes out to any of their numbers or emails,
          whatever is ticked here.
        </p>
      )}
      <div className="nc-consent" role="group" aria-label="What they agreed to">
        <label className="checkrow">
          <input
            type="checkbox"
            checked={f.consent}
            onChange={(e) => setF({ ...f, consent: e.target.checked })}
            disabled={f.doNotContact}
            autoFocus
          />{' '}
          {phone ? 'OK to text' : 'OK to email'} about their appointments and invoices
        </label>
        <label className="checkrow">
          <input
            type="checkbox"
            checked={f.consent && f.marketing}
            onChange={(e) => setF({ ...f, marketing: e.target.checked })}
            disabled={!f.consent || f.doNotContact}
          />{' '}
          Offers and reminders too (needs the first)
        </label>
        <label className="checkrow">
          <input
            type="checkbox"
            checked={f.doNotContact}
            onChange={(e) => setF({ ...f, doNotContact: e.target.checked })}
          />{' '}
          Do not contact {phone ? 'this number' : 'this email'}
        </label>
      </div>
      <div className="fgrid">
        <SourcePicker value={f.source} onChange={(source) => setF({ ...f, source })} />
        {f.doNotContact && !method.doNotContact && (
          <label className="f">
            Why
            <input
              className="field"
              value={f.reason}
              onChange={(e) => setF({ ...f, reason: e.target.value })}
              placeholder="optional, like “Asked on the phone”"
            />
          </label>
        )}
      </div>
      {consentLine(method, longDay) && (
        <p className="small consent-was">Now: {consentLine(method, longDay)}</p>
      )}
      <p className="muted small">
        Saved with your name and the time. Turning consent off also stops messages already waiting to go.
      </p>
      {kept && (
        <p className="notice ok" role="status">
          {kept}
        </p>
      )}
      <Buttons w={w} label="Save" busyLabel="Saving…" onSave={() => void save()} onCancel={onClose} />
    </Sheet>
  );
}

// ── an address ──

export function EditLocation({
  customer,
  location,
  withCodes,
  ownOnly = false,
  onSaved,
  onClose,
  onLatest,
}: {
  customer: Customer;
  location: Loc;
  withCodes: boolean;
  /**
   * While the company mirrors ServiceTitan: only the access notes and codes and the site contact, the
   * address's own columns (ownLocationInputs); the address, its name and a new contact are ServiceTitan's.
   */
  ownOnly?: boolean;
  onSaved: (c: Customer) => void;
  onClose: () => void;
  /** Read the customer again after someone else's change (F-53). */
  onLatest?: () => Promise<unknown>;
}) {
  const [f, setF] = useState(() => locationForm(location));
  useRebase(locationForm(location), location.rowVersion, setF);
  const w = useWrite(JSON.stringify(f));
  // "Someone else…" is added under this id first, then made the site contact: a retry adds them once
  const [someoneId] = useState(newId);
  const save = async () => {
    if (ownOnly) {
      const built = ownLocationInputs(location, f, withCodes);
      if ('error' in built) return void (await w.run('customers.setAccess', built));
      let c: Customer | null = null;
      if (built.access) {
        c = await w.run<Customer>(
          'customers.setAccess',
          { input: built.access },
          built.site ? 'access' : undefined,
        );
        if (!c) return;
      }
      if (built.site) c = await w.run<Customer>('customers.setSiteContact', { input: built.site });
      if (c) onSaved(c);
      return;
    }
    if (f.siteContactId === SOMEONE_ELSE) {
      const added = await w.run<Customer>(
        'customers.addContact',
        someoneInput(location, someoneId, f.someone),
        'contact',
      );
      if (!added) return;
    }
    const c = await w.run<Customer>(
      'customers.updateLocation',
      locationInput(location, f, withCodes, someoneId),
    );
    if (c) onSaved(c);
  };
  return (
    <Sheet title={`${location.line1}, ${location.city}`} onClose={onClose}>
      {ownOnly && <p className="muted small">The address and its name: {MIRROR_LINE}</p>}
      {hasWholeAddress(location) && !ownOnly && (
        <AddressFields a={f.street} onChange={(street) => setF({ ...f, street })} required />
      )}
      <div className="fgrid">
        {!ownOnly && (
          <label className="f">
            Name for this address
            <input
              className="field"
              value={f.name}
              onChange={(e) => setF({ ...f, name: e.target.value })}
              placeholder="optional, like Building B or Cottage"
              autoFocus
            />
          </label>
        )}
        {withCodes && (
          <label className="f">
            Access codes
            <input
              className="field"
              value={f.accessCodes}
              onChange={(e) => setF({ ...f, accessCodes: e.target.value })}
              placeholder="gate, lockbox or alarm"
              autoComplete="off"
            />
          </label>
        )}
      </div>
      <label className="f">
        Access notes
        <textarea
          className="field area"
          value={f.accessNotes}
          onChange={(e) => setF({ ...f, accessNotes: e.target.value })}
          placeholder="Where to park, which door, the dog"
        />
      </label>
      <SiteContactPicker
        customer={customer}
        location={location}
        someone={!ownOnly}
        f={f}
        onChange={(g) => {
          setF(g);
          w.setError(null);
        }}
      />
      <p className="muted small">The technicians’ iPads get the change at their next sync.</p>
      <Buttons
        w={w}
        {...(onLatest ? { onLatest } : {})}
        label="Save"
        busyLabel="Saving…"
        onSave={() => void save()}
        onCancel={onClose}
      />
    </Sheet>
  );
}

/**
 * The address's site contact (F-54): who hears about its visits (the confirmation, the reminder,
 * "on the way") instead of the customer. One of the customer's contacts, the bill-to's, someone only
 * here, or "Someone else…" with their name, phone and consent.
 */
function SiteContactPicker({
  customer,
  location,
  someone = true,
  f,
  onChange,
}: {
  customer: Customer;
  location: Loc;
  /** "Someone else…", a new contact: not while the company mirrors ServiceTitan (contacts are its). */
  someone?: boolean;
  f: ReturnType<typeof locationForm>;
  onChange: (f: ReturnType<typeof locationForm>) => void;
}) {
  const billToId = location.billTo?.customerId;
  const [billTo, setBillTo] = useState<Customer | null>(null);
  useEffect(() => {
    if (!billToId) return;
    // the property manager's people, so their caretaker can be picked
    void client
      .call<Customer>('customers.get', { id: billToId })
      .then(setBillTo)
      .catch(() => setBillTo(null));
  }, [billToId]);
  const groups = siteContactChoices(customer, location, billTo);
  const consentId = useId();
  const one = f.someone;
  const setOne = (patch: Partial<typeof one>) => onChange({ ...f, someone: { ...one, ...patch } });
  return (
    <>
      <label className="f">
        Site contact
        <select
          className="field"
          value={f.siteContactId}
          onChange={(e) => onChange({ ...f, siteContactId: e.target.value })}
        >
          <option value="">Nobody: visit texts go to {customer.displayName}</option>
          {groups.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.people.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name}
                </option>
              ))}
            </optgroup>
          ))}
          {someone && <option value={SOMEONE_ELSE}>Someone else…</option>}
        </select>
      </label>
      <p className="muted small">
        The person at this address, like a tenant or a caretaker. The confirmation, the reminder and “on the
        way” go to them, never to {customer.displayName} instead. Invoices and payment texts stay with the
        account.
      </p>
      {f.siteContactId === SOMEONE_ELSE && (
        <div className="nc-consent" role="group" aria-labelledby={consentId}>
          <div className="flabel" id={consentId}>
            The site contact (only at this address)
          </div>
          <div className="fgrid">
            <label className="f">
              Name
              <input className="field" value={one.name} onChange={(e) => setOne({ name: e.target.value })} />
            </label>
            <label className="f">
              Phone
              <input
                className="field"
                type="tel"
                value={one.phone}
                onChange={(e) => setOne({ phone: e.target.value })}
              />
            </label>
          </div>
          <label className="checkrow">
            <input
              type="checkbox"
              checked={one.sms}
              onChange={(e) => setOne({ sms: e.target.checked })}
              disabled={!one.phone.trim()}
            />{' '}
            OK to text about visits here (saved with who and when)
          </label>
          {one.sms && <SourcePicker value={one.source} onChange={(source) => setOne({ source })} />}
        </div>
      )}
    </>
  );
}

// ── another address ──

export function AddLocation({
  customer,
  withCodes,
  onSaved,
  onClose,
}: {
  customer: Customer;
  withCodes: boolean;
  onSaved: (c: Customer) => void;
  onClose: () => void;
}) {
  const [f, setF] = useState(emptyLocationForm);
  const w = useWrite(JSON.stringify(f));
  const save = async () => {
    const c = await w.run<Customer>('customers.addLocation', newLocationInput(customer, f, withCodes));
    if (c) onSaved(c);
  };
  return (
    <Sheet title={`Another address for ${customer.displayName}`} onClose={onClose}>
      <AddressFields
        a={f.street}
        onChange={(street) => {
          setF({ ...f, street });
          w.setError(null);
        }}
        required
      />
      <div className="fgrid">
        <label className="f">
          Name for this address
          <input
            className="field"
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
            placeholder="optional, like Rental or Cottage"
          />
        </label>
        {withCodes && (
          <label className="f">
            Access codes
            <input
              className="field"
              value={f.accessCodes}
              onChange={(e) => setF({ ...f, accessCodes: e.target.value })}
              placeholder="gate, lockbox or alarm"
              autoComplete="off"
            />
          </label>
        )}
      </div>
      <label className="f">
        Access notes
        <textarea
          className="field area"
          value={f.accessNotes}
          onChange={(e) => setF({ ...f, accessNotes: e.target.value })}
          placeholder="Where to park, which door, the dog"
        />
      </label>
      <Buttons
        w={w}
        label="Add address"
        busyLabel="Adding the address…"
        disabled={!f.street.line1.trim() || !f.street.city.trim()}
        onSave={() => void save()}
        onCancel={onClose}
      />
    </Sheet>
  );
}

// ── equipment at an address ──

export function AddEquipment({
  location,
  equipment,
  today,
  onSaved,
  onClose,
  onLatest,
}: {
  location: Loc;
  /** A unit on record to correct or retire (equipment.update, equipment.retire); without it, a new one. */
  equipment?: Equipment;
  today: string;
  onSaved: () => void;
  onClose: () => void;
  /** Read the customer again after someone else's change (F-53). */
  onLatest?: () => Promise<unknown>;
}) {
  const [f, setF] = useState(() => (equipment ? equipmentForm(equipment) : emptyEquipmentForm()));
  useRebase(equipment ? equipmentForm(equipment) : f, equipment?.rowVersion, setF);
  const w = useWrite(JSON.stringify(f));
  const [retiring, setRetiring] = useState(false);
  const [reason, setReason] = useState('');
  const gone = useWrite(`retire:${reason}`);
  const retire = async () => {
    if (!equipment) return;
    const r = await gone.run('equipment.retire', retireInput(equipment.id, reason));
    if (r) onSaved();
  };
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => {
    setF({ ...f, [k]: e.target.value });
    w.setError(null);
  };
  const save = async () => {
    const r = equipment
      ? await w.run('equipment.update', equipmentChangeInput(equipment, f, today))
      : await w.run<{ id: string }>('equipment.add', equipmentInput(location.id, f, today));
    if (r) onSaved();
  };
  const at = location.name ?? location.line1;
  return (
    <Sheet title={equipment ? `${equipment.kind} at ${at}` : `Equipment at ${at}`} onClose={onClose}>
      <div className="fgrid">
        <label className="f">
          What it is
          <input
            className="field"
            value={f.kind}
            onChange={set('kind')}
            placeholder="Water heater"
            list="equipment-kinds"
            autoFocus
          />
          <datalist id="equipment-kinds">
            {[
              'Water heater',
              'Tankless water heater',
              'Boiler',
              'Backflow preventer',
              'Sump pump',
              'Water softener',
              'Sewer line',
            ].map((k) => (
              <option key={k} value={k} />
            ))}
          </datalist>
        </label>
        <label className="f">
          Make
          <input className="field" value={f.make} onChange={set('make')} placeholder="optional" />
        </label>
        <label className="f">
          Model
          <input className="field" value={f.model} onChange={set('model')} placeholder="optional" />
        </label>
        <label className="f">
          Serial number
          <input className="field" value={f.serial} onChange={set('serial')} placeholder="optional" />
        </label>
        <label className="f">
          Size
          <input
            className="field"
            value={f.size}
            onChange={set('size')}
            placeholder="optional, like 40 gal"
          />
        </label>
        <label className="f">
          Installed
          <input
            className="field"
            type="date"
            value={f.installedOn}
            max={today}
            onChange={set('installedOn')}
          />
        </label>
      </div>
      <label className="f">
        Notes
        <textarea className="field area" value={f.notes} onChange={set('notes')} placeholder="optional" />
      </label>
      <Buttons
        w={w}
        {...(onLatest ? { onLatest } : {})}
        label={equipment ? 'Save the change' : 'Add equipment'}
        busyLabel={equipment ? 'Saving…' : 'Adding…'}
        disabled={!f.kind.trim()}
        onSave={() => void save()}
        onCancel={onClose}
      />
      {equipment && (
        // out of service (replaced, removed): kept on record, off the visit's equipment card
        <div className="contact-remove">
          {retiring ? (
            <>
              <label className="f">
                Why it’s retired
                <input
                  className="field"
                  value={reason}
                  maxLength={300}
                  onChange={(e) => {
                    setReason(e.target.value);
                    gone.setError(null);
                  }}
                  placeholder="like Replaced with a tankless, or Removed"
                  autoFocus
                />
              </label>
              <div className="row">
                <SaveButton
                  className="btn primary danger"
                  busy={gone.busy}
                  busyLabel="Retiring…"
                  error={gone.error}
                  disabled={!reason.trim()}
                  onClick={() => void retire()}
                >
                  Retire it
                </SaveButton>
                <button
                  type="button"
                  className="btn quiet"
                  disabled={gone.busy}
                  onClick={() => setRetiring(false)}
                >
                  Keep it in service
                </button>
              </div>
            </>
          ) : (
            <button type="button" className="btn quiet" onClick={() => setRetiring(true)}>
              Retire this {equipment.kind.toLowerCase()}…
            </button>
          )}
        </div>
      )}
    </Sheet>
  );
}

// ── tags ──

interface Tag {
  id: string;
  name: string;
  color: string | null;
  system: boolean;
  /** false: retired in ServiceTitan (#83), never put on anything new; absent from an older server */
  active?: boolean;
}

export function AddTag({
  customer,
  canCreate,
  onSaved,
  onClose,
}: {
  customer: Customer;
  canCreate: boolean;
  onSaved: (t: Tag) => void;
  onClose: () => void;
}) {
  const [tags, setTags] = useState<Tag[] | null>(null);
  const [pick, setPick] = useState('');
  const [name, setName] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const w = useWrite(`${pick}|${name}`);
  useEffect(() => {
    client
      .call<{ tags: Tag[] }>('tags.list', {})
      // a retired tag stays on what it's on, and isn't offered (#83)
      .then((r) => setTags(r.tags.filter((t) => !t.system && t.active !== false)))
      .catch((e: unknown) => setLoadError(refusal(e)));
  }, []);
  const creating = pick === '__new';
  const save = async () => {
    let tag = tags?.find((t) => t.id === pick) ?? null;
    if (creating) {
      tag = await w.run<Tag>(
        'tags.create',
        name.trim() ? { input: { name: name.trim() } } : { error: 'Name the new tag.' },
        'create',
      );
      if (!tag) return;
      // made: from here on it is picked like any other, so a failed assign doesn't make it twice
      setTags([...(tags ?? []), tag]);
      setPick(tag.id);
    }
    if (!tag) return w.setError('Pick a tag.');
    const r = await w.run<{ assigned: boolean }>(
      'tags.assign',
      { input: { tagId: tag.id, entityType: 'customer', entityId: customer.id } },
      'assign',
    );
    if (r) onSaved(tag);
  };
  return (
    <Sheet title={`Tag ${customer.displayName}`} onClose={onClose}>
      {loadError && <p className="error">{loadError}</p>}
      <label className="f">
        Tag
        <select className="field" value={pick} onChange={(e) => setPick(e.target.value)} autoFocus>
          <option value="">{tags ? 'Pick one' : 'Getting the tags…'}</option>
          {tags?.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
          {canCreate && <option value="__new">A new tag…</option>}
        </select>
      </label>
      {creating && (
        <label className="f">
          New tag’s name
          <input
            className="field"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="like PO required or Gas"
            maxLength={50}
          />
        </label>
      )}
      {creating && <p className="muted small">A new tag is for the whole company to use.</p>}
      <Buttons
        w={w}
        label="Add tag"
        busyLabel="Adding the tag…"
        disabled={!pick || (creating && !name.trim())}
        onSave={() => void save()}
        onCancel={onClose}
      />
    </Sheet>
  );
}

// ── a note on a job ──

export function AddJobNote({
  job,
  onSaved,
  onClose,
}: {
  job: { id: string; title: string };
  onSaved: () => void;
  onClose: () => void;
}) {
  const [body, setBody] = useState('');
  const w = useWrite(body);
  const save = async () => {
    // the save key is the note's own id, so a retry after a lost answer adds it once
    const r = await w.run<{ created: boolean }>('notes.add', noteInput(job.id, body, w.key));
    if (r) onSaved();
  };
  return (
    <Sheet title={`Note on ${job.title}`} onClose={onClose}>
      <label className="f">
        Note
        <textarea
          className="field area"
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            w.setError(null);
          }}
          maxLength={4000}
          placeholder="What the customer said, or a heads-up for the tech"
          autoFocus
        />
      </label>
      <p className="muted small">
        The technician sees it on the job. You can correct it later from History; the earlier words are kept.
      </p>
      <Buttons
        w={w}
        label="Add note"
        busyLabel="Adding the note…"
        disabled={!body.trim()}
        onSave={() => void save()}
        onCancel={onClose}
      />
    </Sheet>
  );
}

// ── a note on the customer themselves ──

export function AddCustomerNote({
  customer,
  onSaved,
  onClose,
}: {
  customer: { id: string; displayName: string };
  onSaved: () => void;
  onClose: () => void;
}) {
  const [body, setBody] = useState('');
  const w = useWrite(body);
  const save = async () => {
    // the save key is the note's own id, so a retry after a lost answer adds it once
    const r = await w.run<{ created: boolean }>(
      'customers.addNote',
      customerNoteInput(customer.id, body, w.key),
    );
    if (r) onSaved();
  };
  return (
    <Sheet title={`Note about ${customer.displayName}`} onClose={onClose}>
      <label className="f">
        Note
        <textarea
          className="field area"
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            w.setError(null);
          }}
          maxLength={4000}
          placeholder="Prefers mornings, or call before coming"
          autoFocus
        />
      </label>
      <p className="muted small">Everyone who opens this customer sees it. Notes can’t be edited later.</p>
      <Buttons
        w={w}
        label="Add note"
        busyLabel="Adding the note…"
        disabled={!body.trim()}
        onSave={() => void save()}
        onCancel={onClose}
      />
    </Sheet>
  );
}

// ── an office file on a job ──

async function sha256(file: File): Promise<string> {
  const d = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(d), (b) => b.toString(16).padStart(2, '0')).join('');
}

export function AddFile({
  jobs,
  onSaved,
  onClose,
}: {
  jobs: { id: string; title: string }[];
  onSaved: () => void;
  onClose: () => void;
}) {
  const [jobId, setJobId] = useState(jobs[0]?.id ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState('document');
  const [caption, setCaption] = useState('');
  // Recorded but not uploaded yet: Upload again picks up from here; Remove takes it back off the job.
  const [held, setHeld] = useState<{ attachmentId: string; fileId: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const upload = async (ids: { attachmentId: string; fileId: string }, f: File) => {
    const u = await client.call<{
      status: string;
      upload: { url: string; method: 'PUT'; headers: Record<string, string> } | null;
    }>('files.createUploadUrl', { fileId: ids.fileId });
    if (u.upload) {
      const put = await fetch(u.upload.url, { method: 'PUT', headers: u.upload.headers, body: f });
      if (!put.ok) throw new Error('upload');
    }
    await client.call('files.confirmUpload', { fileId: ids.fileId });
  };
  const save = async () => {
    if (!file) return setError('Pick a file.');
    // a file is hashed before it goes up, which browsers allow only on a secure page (https)
    if (!crypto.subtle)
      return setError('This page isn’t on a secure (https) connection, so files can’t be added here.');
    const k = fileKind(file.type, file.size);
    if ('error' in k) return setError(k.error);
    setBusy(true);
    setError(null);
    const ids = held ?? { attachmentId: newId(), fileId: newId() };
    try {
      if (!held) {
        await client.call(
          'attachments.add',
          {
            ...ids,
            jobId,
            category,
            kind: k.kind,
            contentType: file.type,
            bytes: file.size,
            sha256: await sha256(file),
            ...(caption.trim() ? { caption: caption.trim() } : {}),
          },
          { idempotencyKey: ids.attachmentId },
        );
        setHeld(ids);
      }
      await upload(ids, file);
      onSaved();
    } catch (e) {
      setError(
        e instanceof Error && e.message === 'upload'
          ? 'The file is on the job but didn’t upload. Upload again, or remove it.'
          : refusal(e),
      );
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!held) return;
    setBusy(true);
    try {
      await client.call('attachments.remove', { attachmentId: held.attachmentId });
      setHeld(null);
      setError(null);
      onClose();
    } catch (e) {
      setError(refusal(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet title="Add a file to a job" onClose={onClose}>
      <div className="fgrid">
        <label className="f">
          Job
          <select
            className="field"
            value={jobId}
            onChange={(e) => setJobId(e.target.value)}
            disabled={!!held}
          >
            {jobs.map((j) => (
              <option key={j.id} value={j.id}>
                {j.title}
              </option>
            ))}
          </select>
        </label>
        <label className="f">
          What it is
          <select
            className="field"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={!!held}
          >
            {FILE_CATEGORIES.map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="f">
        File (photo, PDF or video)
        <input
          className="field file"
          type="file"
          accept={FILE_ACCEPT}
          disabled={!!held}
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            setError(null);
          }}
        />
      </label>
      <label className="f">
        Caption
        <input
          className="field"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="optional, like Signed quote"
          maxLength={500}
          disabled={!!held}
        />
      </label>
      <div className="row">
        <SaveButton
          busy={busy}
          busyLabel="Uploading…"
          error={error}
          disabled={!file || !jobId}
          onClick={() => void save()}
        >
          {held ? 'Upload again' : 'Add file'}
        </SaveButton>
        {held ? (
          <button className="btn quiet" type="button" onClick={() => void remove()} disabled={busy}>
            Remove it
          </button>
        ) : (
          <button className="btn quiet" type="button" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        )}
      </div>
    </Sheet>
  );
}

const longDay = (iso: string) =>
  new Intl.DateTimeFormat('en-CA', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));

// ── a contact's name and role ──

export function RenameContact({
  contact,
  only = false,
  onSaved,
  onClose,
  onLatest,
}: {
  /** As last read: "Get the latest" brings the form onto it, keeping what was typed (QA-697). */
  contact: { id: string; name: string; title: string | null; rowVersion?: number };
  /** The customer's only contact: it can't be removed (the server refuses it too). */
  only?: boolean;
  onSaved: (c: Customer) => void;
  onClose: () => void;
  /** Reads the customer again after a stale refusal ("Someone else changed this contact…"). */
  onLatest?: () => Promise<unknown>;
}) {
  const [f, setF] = useState({ name: contact.name, title: contact.title ?? '' });
  useRebase({ name: contact.name, title: contact.title ?? '' }, contact.rowVersion, setF);
  const { name, title } = f;
  const setName = (v: string) => setF((x) => ({ ...x, name: v }));
  const setTitle = (v: string) => setF((x) => ({ ...x, title: v }));
  const w = useWrite(`${name}|${title}`);
  // another phone or email for them (customers.addContactMethod), and removing them (removeContact)
  const [m, setM] = useState(emptyMethodForm);
  const add = useWrite(JSON.stringify(m));
  const [removing, setRemoving] = useState(false);
  const gone = useWrite(`remove:${contact.id}`);
  const save = async () => {
    const c = await w.run<Customer>('customers.updateContact', renameInput(contact, name, title));
    if (c) onSaved(c);
  };
  const addMethod = async () => {
    const c = await add.run<Customer>('customers.addContactMethod', methodInput(contact.id, m));
    if (c) onSaved(c);
  };
  const remove = async () => {
    const c = await gone.run<Customer>('customers.removeContact', { input: { contactId: contact.id } });
    if (c) onSaved(c);
  };
  const phone = m.kind === 'phone';
  return (
    <Sheet title={`Edit ${contact.name}`} onClose={onClose}>
      <div className="fgrid">
        <label className="f">
          Name
          <input className="field" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <label className="f">
          Role
          <input
            className="field"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="optional, like Tenant or Property manager"
          />
        </label>
      </div>
      <Buttons
        w={w}
        label="Save"
        busyLabel="Saving…"
        disabled={!name.trim()}
        onSave={() => void save()}
        onCancel={onClose}
        {...(onLatest ? { onLatest } : {})}
      />
      <fieldset className="plain contact-more">
        <legend className="flabel">Another phone or email for {contact.name}</legend>
        <div className="fgrid">
          <label className="f">
            Kind
            <select
              className="field"
              value={m.kind}
              onChange={(e) => {
                setM({ ...m, kind: e.target.value as MethodForm['kind'] });
                add.setError(null);
              }}
            >
              <option value="phone">Phone</option>
              <option value="email">Email</option>
            </select>
          </label>
          <label className="f">
            {phone ? 'Phone' : 'Email'}
            <input
              className="field"
              type={phone ? 'tel' : 'email'}
              value={m.value}
              onChange={(e) => {
                setM({ ...m, value: e.target.value });
                add.setError(null);
              }}
            />
          </label>
        </div>
        <label className="checkrow">
          <input
            type="checkbox"
            checked={m.consent}
            onChange={(e) => setM({ ...m, consent: e.target.checked })}
          />{' '}
          {phone ? 'OK to text' : 'OK to email'} about their appointments and invoices
        </label>
        {m.consent && <SourcePicker value={m.source} onChange={(source) => setM({ ...m, source })} />}
        <div className="row">
          <SaveButton
            className="btn soft"
            busy={add.busy}
            busyLabel="Adding…"
            error={add.error}
            disabled={!m.value.trim()}
            onClick={() => void addMethod()}
          >
            {phone ? 'Add the phone' : 'Add the email'}
          </SaveButton>
        </div>
      </fieldset>
      <div className="contact-remove">
        {only ? (
          <p className="muted small">
            {contact.name} is the customer’s only contact: add another before removing them.
          </p>
        ) : removing ? (
          <>
            <p className="small">
              Remove {contact.name} and their phones and emails from this customer? It’s kept in the history.
            </p>
            <div className="row">
              <SaveButton
                className="btn primary danger"
                busy={gone.busy}
                busyLabel="Removing…"
                error={gone.error}
                onClick={() => void remove()}
              >
                Remove {contact.name}
              </SaveButton>
              <button
                type="button"
                className="btn quiet"
                disabled={gone.busy}
                onClick={() => setRemoving(false)}
              >
                Keep them
              </button>
            </div>
          </>
        ) : (
          <button type="button" className="btn quiet" onClick={() => setRemoving(true)}>
            Remove {contact.name}…
          </button>
        )}
      </div>
    </Sheet>
  );
}

// ── one phone or email: corrected, made primary, or removed ──

export function EditMethod({
  contactName,
  method,
  onSaved,
  onClose,
}: {
  contactName: string;
  method: ContactMethod;
  onSaved: (c: Customer) => void;
  onClose: () => void;
}) {
  const phone = method.kind === 'phone';
  const shown = phone ? phoneText(method.value) : method.value;
  const [value, setValue] = useState(shown);
  const w = useWrite(`value:${value}`);
  const other = useWrite(`method:${method.id}`);
  const [removing, setRemoving] = useState(false);
  const change = async () => {
    const c = await w.run<Customer>('customers.updateContactMethod', changeMethodInput(method, value));
    if (c) onSaved(c);
  };
  const primary = async () => {
    const c = await other.run<Customer>(
      'customers.setPrimaryMethod',
      { input: { methodId: method.id } },
      'primary',
    );
    if (c) onSaved(c);
  };
  const remove = async () => {
    const c = await other.run<Customer>(
      'customers.removeContactMethod',
      { input: { methodId: method.id } },
      'remove',
    );
    if (c) onSaved(c);
  };
  return (
    <Sheet title={`${contactName}’s ${phone ? 'phone' : 'email'} ${shown}`} onClose={onClose}>
      <label className="f">
        {phone ? 'Phone' : 'Email'}
        <input
          className="field"
          type={phone ? 'tel' : 'email'}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            w.setError(null);
          }}
          autoFocus
        />
      </label>
      <p className="muted small">
        A changed {phone ? 'number' : 'email'} starts with no consent: ask them again, then record it under
        Consent.
      </p>
      <Buttons
        w={w}
        label="Save the change"
        busyLabel="Saving…"
        onSave={() => void change()}
        onCancel={onClose}
      />
      <div className="row method-more">
        {!method.isPrimary && (
          <button type="button" className="btn soft" disabled={other.busy} onClick={() => void primary()}>
            Make it the primary {phone ? 'phone' : 'email'}
          </button>
        )}
        {removing ? (
          <>
            <span className="small">Remove {shown}?</span>
            <button
              type="button"
              className="btn primary danger"
              disabled={other.busy}
              onClick={() => void remove()}
            >
              {other.busy ? 'Removing…' : 'Remove it'}
            </button>
            <button type="button" className="btn quiet" onClick={() => setRemoving(false)}>
              Keep it
            </button>
          </>
        ) : (
          <button type="button" className="btn quiet" onClick={() => setRemoving(true)}>
            Remove this {phone ? 'phone' : 'email'}…
          </button>
        )}
      </div>
      {other.error && <p className="error small">{other.error}</p>}
    </Sheet>
  );
}

// ── a completed job reopened for a warranty callback, from the customer's history (F-31) ──

/** Review's "Reopen for a warranty callback" from the customer's history: the same words and action. */
export function ReopenJob({
  job,
  onSaved,
  onClose,
}: {
  job: { id: string; title: string };
  onSaved: (said: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const w = useWrite(reason.trim());
  // a history row's title is "#<number> · <job type>"
  const number = (job.title.split(' · ')[0] ?? '').replace(/^#/, '');
  const save = async () => {
    const r = await w.run<{ status: string }>('jobs.reopen', reopenInput(job.id, reason));
    if (r) onSaved(`Job #${number} is reopened. Add a visit to it in Book.`);
  };
  return (
    <Sheet title={`Reopen job #${number} for a warranty callback`} onClose={onClose}>
      <p className="muted small">
        The job goes back to in progress; its finalized invoice and locked splits stay as they are. Book a
        visit on it afterwards.
      </p>
      <label className="f">
        Why it’s being reopened
        <textarea
          className="field area"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            w.setError(null);
          }}
          maxLength={500}
          autoFocus
        />
      </label>
      <Buttons
        w={w}
        label="Reopen job"
        busyLabel="Reopening…"
        disabled={!reason.trim()}
        onSave={() => void save()}
        onCancel={onClose}
      />
    </Sheet>
  );
}

// ── billing terms (F-29) ──

/** Days to pay and the PO rule, for whoever is billed: this customer's own work and the addresses they're the bill-to of. */
export function EditTerms({
  customer,
  onSaved,
  onClose,
}: {
  customer: Customer;
  onSaved: (said: string) => void;
  onClose: () => void;
}) {
  const [f, setF] = useState(() => termsForm(customer.billingTerms));
  const w = useWrite(JSON.stringify(f));
  const save = async () => {
    const r = await w.run<{ jobsUpdated: number }>(
      'customers.setBillingTerms',
      termsInput(customer.id, customer.billingTerms, f),
    );
    if (r)
      onSaved(
        r.jobsUpdated
          ? `Billing terms saved. ${r.jobsUpdated === 1 ? 'One open job follows' : `${r.jobsUpdated} open jobs follow`} the PO rule now.`
          : 'Billing terms saved.',
      );
  };
  return (
    <Sheet title={`Billing terms for ${customer.displayName}`} onClose={onClose}>
      <div className="fgrid">
        <label className="f">
          Days to pay
          <select
            className="field"
            value={f.pick}
            onChange={(e) => {
              setF({ ...f, pick: e.target.value });
              w.setError(null);
            }}
            autoFocus
          >
            {TERMS_DAYS.map((d) => (
              <option key={d} value={String(d)}>
                {d ? `Net ${d}` : 'Due on receipt'}
              </option>
            ))}
            <option value="other">Another number of days…</option>
          </select>
        </label>
        {f.pick === 'other' && (
          <label className="f">
            Days
            <input
              className="field"
              inputMode="numeric"
              value={f.days}
              onChange={(e) => {
                setF({ ...f, days: e.target.value });
                w.setError(null);
              }}
              placeholder="like 90"
            />
          </label>
        )}
      </div>
      <label className="checkrow">
        <input
          type="checkbox"
          checked={f.poRequired}
          onChange={(e) => {
            setF({ ...f, poRequired: e.target.checked });
            w.setError(null);
          }}
        />{' '}
        They need a PO on every job (Review won’t complete one without it)
      </label>
      <p className="muted small">
        For their own work and every address they’re billed for. Open jobs follow at once; invoices already
        sent keep the terms they went out on.
      </p>
      <Buttons w={w} label="Save" busyLabel="Saving…" onSave={() => void save()} onCancel={onClose} />
    </Sheet>
  );
}

// ── tax exemption (BL-094) ──

/** Whether the company charges them tax, and if not, the exemption's reference and why. */
export function EditTaxExempt({
  customer,
  onSaved,
  onClose,
}: {
  customer: Customer;
  onSaved: (said: string) => void;
  onClose: () => void;
}) {
  const [f, setF] = useState(() => taxExemptForm(customer.taxExempt));
  const w = useWrite(JSON.stringify(f));
  const save = async () => {
    const r = await w.run<{ words: string | null }>(
      'customers.setTaxExempt',
      taxExemptInput(customer.id, customer.taxExempt, f),
    );
    if (r)
      onSaved(
        r.words
          ? `Saved. New invoices and estimates say “${r.words}”.`
          : 'Saved. New invoices and estimates are taxed.',
      );
  };
  const set = (x: Partial<typeof f>) => {
    setF({ ...f, ...x });
    w.setError(null);
  };
  return (
    <Sheet title={`Tax for ${customer.displayName}`} onClose={onClose}>
      <label className="checkrow">
        <input
          type="checkbox"
          checked={f.exempt}
          onChange={(e) => set({ exempt: e.target.checked })}
          autoFocus
        />{' '}
        Tax exempt: charge them no tax
      </label>
      {f.exempt && (
        <div className="fgrid">
          <label className="f">
            Reference
            <input
              className="field"
              value={f.reference}
              onChange={(e) => set({ reference: e.target.value })}
              placeholder="a status card or certificate number"
            />
          </label>
          <label className="f">
            Why
            <input
              className="field"
              value={f.reason}
              onChange={(e) => set({ reason: e.target.value })}
              placeholder="like Status card"
            />
          </label>
        </div>
      )}
      <p className="muted small">
        For their own work and every address they’re billed for. Invoices and estimates made from now on
        follow it; ones already made keep what they say.
      </p>
      <Buttons w={w} label="Save" busyLabel="Saving…" onSave={() => void save()} onCancel={onClose} />
    </Sheet>
  );
}

// ── a note on a job, corrected or removed from History (notes.update, notes.delete) ──

export function EditJobNote({
  note,
  body: was,
  jobTitle,
  onSaved,
  onClose,
  onLatest,
}: {
  note: { id: string; rowVersion: number };
  body: string;
  jobTitle: string;
  onSaved: (said: string) => void;
  onClose: () => void;
  /** Read the note again after someone else's correction (F-53). */
  onLatest?: () => Promise<unknown>;
}) {
  const [body, setBody] = useState(was);
  // read again: the words typed stay, else the latest words (F-53)
  useRebase({ body: was }, note.rowVersion, (u) => setBody((b) => u({ body: b }).body));
  const w = useWrite(body);
  const gone = useWrite(`remove:${note.id}`);
  const [removing, setRemoving] = useState(false);
  const save = async () => {
    const r = await w.run('notes.update', noteEditInput(note, was, body));
    if (r) onSaved('Note corrected. Its earlier words are in the notes’ history.');
  };
  const remove = async () => {
    const r = await gone.run('notes.delete', { input: { noteId: note.id } });
    if (r) onSaved('Note removed. Its words are kept in the notes’ history.');
  };
  return (
    <Sheet title={`Correct the ${jobTitle.charAt(0).toLowerCase()}${jobTitle.slice(1)}`} onClose={onClose}>
      <label className="f">
        Note
        <textarea
          className="field area"
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            w.setError(null);
          }}
          maxLength={4000}
          autoFocus
        />
      </label>
      <p className="muted small">The technician sees the change on the job. The earlier words are kept.</p>
      <Buttons
        w={w}
        {...(onLatest ? { onLatest } : {})}
        label="Save the correction"
        busyLabel="Saving…"
        disabled={!body.trim()}
        onSave={() => void save()}
        onCancel={onClose}
      />
      <div className="contact-remove">
        {removing ? (
          <div className="row">
            <SaveButton
              className="btn primary danger"
              busy={gone.busy}
              busyLabel="Removing…"
              error={gone.error}
              onClick={() => void remove()}
            >
              Remove the note
            </SaveButton>
            <button
              type="button"
              className="btn quiet"
              disabled={gone.busy}
              onClick={() => setRemoving(false)}
            >
              Keep it
            </button>
          </div>
        ) : (
          <button type="button" className="btn quiet" onClick={() => setRemoving(true)}>
            Remove this note…
          </button>
        )}
      </div>
    </Sheet>
  );
}
